import { useDispatch } from '@redux';
import crudActions from '@redux/crudActions';
import { Button, Form, InputNumber, Table } from 'antd';
import ModalCustom from 'components/commons/ModalCustom';
import useToggleModal from 'hooks/useToogleModal';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import VariantNameItem from './VariantNameItem';
import { uniqBy } from 'lodash';

interface IProductVariantsSelectionModalProps {
  onApplyVariants: (variants: DynamicObjectType[]) => void;
  onApplyOrder: (order: DynamicObjectType) => void;
  variantSelected?: DynamicObjectType[];
  isEdit?: boolean;
}

const ProductVariantsSelectionModal = (
  {
    onApplyVariants,
    onApplyOrder,
    variantSelected,
    isEdit,
  }: IProductVariantsSelectionModalProps,
  ref,
) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { visible, toggleModal } = useToggleModal();

  const [form] = Form.useForm();

  const [orderId, setOrderId] = useState();
  const [variantsData, setVariantsData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentOrder, setCurrentOrder] = useState({});

  const onCancel = () => {
    toggleModal();
    // form.resetFields();
    setSelectedRowKeys([]);
    setVariantsData([]);
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (id) => {
        toggleModal();
        setOrderId(id);
      },
      close: () => {
        toggleModal();
      },
      clearSelectedRow: () => {
        setSelectedRowKeys([]);
      },
      clearFormValues: () => {
        form.resetFields();
      },
    }),
    [],
  );

  const getOrderDetail = () => {
    dispatch(
      crudActions.orders.getDataById({
        data: {
          id: orderId,
        },
      }),
    ).then((res: DynamicObjectType) => {
      if (!res?.payload?.error) {
        const compareData = uniqBy(
          [...(res?.payload?.data?.items || []), ...variantSelected],
          'id',
        );
        const variantsData = isEdit ? res?.payload?.data?.items : compareData;
        setCurrentOrder(res?.payload?.data || {});
        setVariantsData(
          variantsData?.map((i) => ({
            ...i,
            quantityEdited: i?.quantityEdited || i?.quantity,
          })) || [],
        );
      }
    });
  };

  useEffect(() => {
    visible && orderId && getOrderDetail();
    if (variantSelected?.length > 0) {
      setSelectedRowKeys(variantSelected?.map((i) => i?.id) || []);
    }
  }, [visible, orderId]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (d, rc) => <VariantNameItem item={rc} />,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantityEdited',
      key: 'quantityEdited',
      render: (quantityEdited, item, index) => {
        return (
          <>
            <Form.Item
              name={['variantData', index, 'id']}
              hidden
              initialValue={item?.id}
            ></Form.Item>
            <Form.Item
              name={['variantData', index, 'quantity']}
              hidden
              initialValue={item?.quantity}
            ></Form.Item>
            <Form.Item
              name={['variantData', index, 'quantityEdited']}
              noStyle
              initialValue={quantityEdited}
            >
              <InputNumber style={{ width: 70 }} min={1} max={item?.quantity} />
            </Form.Item>
          </>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onFinish = (values) => {
    const filterData = variantsData
      ?.filter((item) => selectedRowKeys.includes(item?.id))
      ?.map((item) => ({
        ...item,
        quantityEdited:
          values?.variantData?.find((i) => i?.id === item?.id)
            ?.quantityEdited || item?.quantityEdited,
      }));
    onApplyVariants(filterData);
    onApplyOrder(currentOrder);
    onCancel();
  };

  return (
    <ModalCustom
      open={visible}
      onCancel={onCancel}
      title={t('transferOrders.addProduct')}
      width={700}
      // onOk={() => form.submit()}
      footer={null}
      centered
    >
      <Form form={form} onFinish={onFinish}>
        <div className="flex justify-between px-2.5 py-[5px] border border-[--border-color-light] bg-[--border-color-light] rounded-[5px] items-center">
          <div className="text-[--text-title-gray]">{`${selectedRowKeys?.length} selected`}</div>
          <Button className="btn-primary-light" onClick={() => form.submit()}>
            {t('button.add')}
          </Button>
        </div>
        <Table
          dataSource={variantsData}
          columns={columns}
          rowSelection={rowSelection}
          rowKey="id"
        />
      </Form>
    </ModalCustom>
  );
};

export default forwardRef(ProductVariantsSelectionModal);
